import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/* Context */
import { useAuthContext } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

/* API helper */
import { api } from "../api";

/* Utils */
import { generateInitialAvatar } from "../utils/avatarUtils";

/* CSS */
import AuthHeader from '../components/layout/AuthHeader';
import shared from '../styles/shared.module.css';
import styles from './ProfileSetupPage.module.css';

/* Icons */
import add_clicked from '../assets/icons/add_clicked.svg';
import add_unclicked from '../assets/icons/add_unclicked.svg';
import arrow_back from '../assets/icons/arrow_back.svg';

const ProfileSetupPage = () => {

    const navigate = useNavigate();
    const location = useLocation();

    const { login, updateUser } = useAuthContext();
    const { showToast } = useToast();

    const [username, setUsername] = useState("");
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [isHoveringAvatar, setIsHoveringAvatar] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Grab email and password from previous step
    const email = location.state?.email || "";
    const password = location.state?.password || "";
    const next = location.state?.next || null;

    const hasCustomAvatar = avatarFile !== null;

    // A ref to securely target our hidden file input
    const fileInputRef = useRef(null);

    // What happens when the user picks a file
    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Memory cleanup: if a custom image already exists, delete the old one from memory
            if (avatarPreview) {
                URL.revokeObjectURL(avatarPreview);
            }

            setAvatarPreview(URL.createObjectURL(file));
            setAvatarFile(file);
        }
    };

    return (
        <>
            <AuthHeader currentStep="profile" />

            <div className={shared.authBody}>
                <div className={shared.authTitleWrapper}>
                    <button
                        className={shared.backButton}
                        onClick={() => window.history.back()}
                    >
                        <img
                            src={arrow_back}
                            alt="Back to previous page"
                            className={shared.backArrow}
                        />
                        Back
                    </button>

                    <div className={shared.authTitle} style={{ fontWeight: '900' }}>Set up your profile</div>
                </div>

                <div className={shared.authBodyInput}>

                    {/* Username field */}
                    <div className={shared.authField}>
                        <div className={shared.authFieldLabel} style={{ fontWeight: '600' }}>Username</div>
                        <input
                            type="text"
                            className={shared.authFieldInput}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                        />
                    </div>

                    {/* Avatar field */}
                    <div className={shared.authField}>
                        <div className={shared.authFieldLabel} style={{ fontWeight: '600' }}>Avatar (optional)</div>
                        <div className={shared.authSubTitleText}>Helps visually distinguish you out of all your travel buddies.</div>
                        <div className={styles.avatarWrapper}>
                            {/* The ugly hidden file input */}
                            <input
                                type="file"
                                accept="image/png, image/jpeg"
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                                onChange={handleAvatarChange}
                            />
                            {/* The relative wrapper that holds BOTH the Avatar and the Plus Icon */}
                            <div
                                className={styles.avatarImageWrapper}
                                onMouseEnter={() => setIsHoveringAvatar(true)}
                                onMouseLeave={() => setIsHoveringAvatar(false)}
                                onClick={() => fileInputRef.current.click()}
                            >
                                <img
                                    className={styles.avatar}
                                    src={hasCustomAvatar ? avatarPreview : generateInitialAvatar(username)}
                                    alt="Profile avatar"
                                />
                                <img
                                    className={styles.addIcon}
                                    src={isHoveringAvatar ? add_clicked : add_unclicked}
                                    alt="Add avatar"
                                />
                            </div>
                            {/* Remove image */}
                            <div
                                className={`${styles.removeImage} ${!hasCustomAvatar ? styles.disabledText : ''}`}
                                onClick={() => {
                                    // Only allow removing if they actually have a custom image
                                    if (hasCustomAvatar) {
                                        // Memory cleanup: delete the custom image from memory
                                        URL.revokeObjectURL(avatarPreview);
                                        setAvatarPreview(null);
                                        setAvatarFile(null);
                                        fileInputRef.current.value = ""; // Clears the file input memory
                                    }
                                }}
                            >
                                Remove image
                            </div>
                        </div>
                    </div>

                    {/* Submit button */}
                    <button
                        className={shared.authButton}
                        style={{ marginTop: '16px' }}
                        disabled={username.trim() === ""}
                        onClick={async () => {
                            try {
                                setIsLoading(true);

                                // Register user
                                const data = await api.post('/auth/register', {
                                    email,
                                    password,
                                    username
                                });
                                login(data.token, data.user);

                                if (hasCustomAvatar) {
                                    try {
                                        // Pack the file into the FormData envelope
                                        const formData = new FormData();
                                        formData.append('image', avatarFile)

                                        // No header as the browser automatically handles this
                                        const imageData = await api.upload('/images', formData);

                                        // Load avatar to user profile
                                        const result = await api.patch('/auth/profile', {
                                            username: username,
                                            profile_picture_url: imageData.url
                                        });
                                        updateUser(result.user);

                                        // Memory cleanup: the preview blob is no longer needed
                                        URL.revokeObjectURL(avatarPreview);
                                    } catch (avatarErr) {
                                        console.error("Error saving avatar: ", avatarErr);
                                        showToast(
                                            `Account created, but your picture wasn't saved${avatarErr.message ? ` (${avatarErr.message})` : ''}. You can add it in Settings.`,
                                            'info',
                                            5000
                                        );
                                    }
                                }
                                navigate(next || '/');
                            } catch (err) {
                                console.error("Error registering: ", err);
                                showToast(err.message || "Error creating account. Please try again.", 'error');
                            } finally {
                                setIsLoading(false);
                            }
                        }}
                    >
                        <button
                            type="submit"
                            className={`${shared.authButton} ${isLoading ? shared.buttonLoading : ''}`}
                            disabled={username.trim() === ""}
                        >
                            <div className={shared.buttonContent}>
                                {isLoading ? "Creating account..." : "Done"}
                                {isLoading && <span className={shared.spinner}></span>}
                            </div>
                        </button>
                    </button>
                </div>
            </div>
        </>
    );
};

export default ProfileSetupPage;
